import PropTypes from 'prop-types'
import { useFormikContext } from 'formik'
import { Button } from 'antd'
import Text from 'antd/lib/typography/Text'
import { createUseStyles } from 'react-jss'
import { DatePicker, Input } from '../Forms'
import Select from '../Forms/Select'
import TextArea from '../Forms/TextArea'
import MultipleCheckboxes from '../Forms/MultipleCheckboxes'
import RadioButton from '../Forms/RadioButton'
import Checkbox from '../Forms/Checkbox'
import { OTHER_OPTION_VALUE, OTHERS_SET_KEY, getOptions } from './form-utils'

const TEXT_AREA_SIZE = { minRows: 2, maxRows: 8 }

function formatSelectOptions(options) {
  return (options || []).map(name => ({
    id: name,
    name,
  }))
}

const useStyles = createUseStyles({
  suggestionsText: {
    color: '#5175B3',
  },
  suggestion: {
    width: '90%',
    margin: '1rem',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    overflowY: 'auto',
    height: '3rem',
  },
})

export default function FormItem({
  field,
  propsFor,
  formType,
  suggestions = [],
  layout = 'horizontal',
}) {
  const { values, setFieldValue } = useFormikContext()
  const { dataType, itemName, itemId, options, description } = field
  const classes = useStyles()

  const toggleSuggestion = (currentValue, suggestionValue) => {
    if (!currentValue) return suggestionValue

    const suggestionRegex = new RegExp(
      `(^|\\s)${suggestionValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`,
    )

    if (suggestionRegex.test(currentValue)) {
      let newValue = currentValue.replace(suggestionRegex, ' ').trim()
      newValue = newValue.replace(/\s{2,}/g, ' ')
      return newValue
    }

    const separator = currentValue.endsWith(' ') ? '' : ' '
    return `${currentValue}${separator}${suggestionValue}`
  }

  if (dataType === 'date') {
    return (
      <DatePicker
        {...propsFor(itemId)}
        helperText={description}
        allowClear={false}
        format="MM/DD/YYYY"
        layout={layout}
      />
    )
  }

  if (dataType === 'long_text') {
    return (
      <>
        <TextArea
          {...propsFor(itemId)}
          helperText={description}
          autoSize={TEXT_AREA_SIZE}
          layout={layout}
        />
        {suggestions.length > 0 && (
          <>
            <Text className={classes.suggestionsText}>
              Or choose from the following suggestions:
            </Text>

            {suggestions.map(suggestion => (
              <Button
                className={classes.suggestion}
                key={suggestion}
                type={
                  values[itemId]?.includes(suggestion.value)
                    ? 'primary'
                    : 'default'
                }
                onClick={() => {
                  const currentValue = values[itemId] || ''
                  const newValue = toggleSuggestion(
                    currentValue,
                    suggestion.value,
                  )
                  setFieldValue(itemId, newValue)
                }}
              >
                {suggestion.value}
              </Button>
            ))}
          </>
        )}
      </>
    )
  }

  if (dataType === 'choice') {
    const hasOtherOption = values[itemId] === OTHER_OPTION_VALUE

    return (
      <>
        <Select
          {...propsFor(itemId)}
          options={formatSelectOptions(getOptions(options, formType))}
          helperText={description}
          layout="vertical"
        />
        {hasOtherOption && (
          <Input
            name={`${OTHERS_SET_KEY}.${itemId}`}
            {...propsFor(`${OTHERS_SET_KEY}.${itemId}`)}
          />
        )}
      </>
    )
  }

  if (dataType === 'multiple_choice') {
    return (
      <MultipleCheckboxes
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...propsFor(itemId)}
        options={getOptions(options, formType)}
        helperText={description}
        layout="vertical"
      />
    )
  }

  if (dataType === 'single_choice') {
    return (
      <RadioButton
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...propsFor(itemId)}
        options={getOptions(options, formType)}
        helperText={description}
        layout="vertical"
      />
    )
  }

  if (dataType === 'yes_or_no') {
    return (
      <Checkbox
        {...propsFor(itemId)}
        options={getOptions(options, formType)}
        helperText={description}
        layout="vertical"
      />
    )
  }

  return (
    <Input
      name={itemId}
      placeholder={itemName}
      helperText={description}
      layout={layout}
      {...propsFor(itemId)}
    />
  )
}

FormItem.propTypes = {
  field: PropTypes.shape({
    dataType: PropTypes.string.isRequired,
    itemName: PropTypes.string.isRequired,
    itemId: PropTypes.string.isRequired,
    options: PropTypes.shape(),
    description: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  formType: PropTypes.string.isRequired,
  propsFor: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape()),
  layout: PropTypes.string,
}
